import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { CartTransactionService, PrettyCartTransaction } from '../services/cart-transaction.service';
import { AlertsService } from '../../services/alerts.service';
import { RoutingService} from '../../services/routing.service';
import { CartService } from '../services/cart.service';
import { BlockUIService } from '../../layout/block-ui/block-ui.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, Observable } from 'rxjs';

export interface EmailInput {
  email2: string | null;
  email: string | null;
}

@Component({
  selector: 'checkoutcomplete',
  templateUrl: './cart-checkout-complete.component.html',
  styleUrls: ['./cart-checkout-complete.component.css']
})
export class CartCheckoutCompleteComponent implements OnInit, OnDestroy {
  user: any;
  errorMsg: string | null = null;
  pageList: any[] = [];
  input: EmailInput = {
	  email2: null,
	  email:null
  };
  isKiosk: boolean = false;
  executed: boolean | null = null;
  newPurchase: any;
  emailSent: boolean = false;
  private userPrefsUpdateSubscription: Subscription = new Subscription();
  loading$: Observable<boolean>;
  message$: Observable<string>;
  LOCALIZATION: any = {};
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private transactionService: CartTransactionService,
    private alertsService: AlertsService,
    private cartService: CartService,
    private blockUI: BlockUIService,
    private routingService: RoutingService,
    private sanitizer: DomSanitizer
  ) {
	this.loading$ = blockUI.loading;
    this.message$ = blockUI.message;
    this.user = this.userService.user;
    this.LOCALIZATION = this.userService.LOCALIZATION;
  }
/* to test use https://server/checkout-complete?transaction=2613b665-f19f-4dbd-93a0-00d72247b7d9&PNREF=AD0A0D7D9C98&SECURETOKEN=DLHbLAz13SUSksufHsOotDguL&SECURETOKENID=9x057u
   change transaction parameter to existing PENDING transaction
*/
  ngOnInit(): void {
    const params = this.routingService.getQueryParams();
    this.isKiosk = params['kiosk'] === 'true';
    this.errorMsg = params['errorMsg'] || null;

    // Handle PayPal URL Fix (if needed), # not used in URL anymore
    /*if (window.location.search !== '') {
      const hash = window.location.hash;
      const search = window.location.search.substring(1);
      window.location.href = this.sanitizer.sanitize(1, `${window.location.origin}${window.location.pathname}${hash}&${search}`) || '';
    }*/

    setTimeout(() => {
       this.blockUI.start(this.LOCALIZATION.orderCompleting);
    });
    //transaction already completed and purchase was provided;
    if (params['newPurchase']) {
      this.newPurchase = JSON.parse(params['newPurchase']);
      this.processPurchase(this.newPurchase, false);
    } else if (this.user?.permissions) {
	  //else lets wait for user info to be retrieved and complete transaction;
      this.submitForCompletion();
    } else {
		// Subscribe to user preferences update
      this.userPrefsUpdateSubscription = this.userService.broadcastEvent.subscribe((eventId) => {
	      if("userPrefsUpdate" === eventId){
	      	this.user = this.userService.user;
            this.submitForCompletion();
	      }
      });
    }
  }
  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.userPrefsUpdateSubscription) {
      this.userPrefsUpdateSubscription.unsubscribe();
    }
  }
  private processPurchase(purchase: any, isPretty: boolean): void {
    const pretty = !isPretty? this.transactionService.prettyTransaction(purchase):purchase;
    pretty.description.forEach((document: any) => {
      document.allPages.forEach((page: any) => {
        this.pageList.push({
          title: `${document.title} ${page.title}`,
          amount: page.amount,
          download: page.download,
          downloadPDF: page.downloadPDF
        });
      });
    });

    this.newPurchase.created = new Date(this.newPurchase.created).toLocaleDateString('en-US');
    this.executed = true;
    this.cartService.clearCart();
    this.blockUI.stop();
  }

  private submitForCompletion(): void {
    const params = this.routingService.getQueryParams();
    if (params['transaction'] && params['PNREF'] && params['SECURETOKEN']) {
      this.transactionService.executePayPalPayment(params['PNREF'], params['SECURETOKEN'], params['transaction'])
        .then((newPurchase: PrettyCartTransaction) => {
          this.newPurchase = newPurchase;
          this.processPurchase(newPurchase, true);
        })
        .catch(() => {
          this.errorMsg = "Failed to complete order. Try again.";
          this.executed = false;
          this.blockUI.stop();
        });
    } else {
      this.errorMsg = "Invalid transaction.";
      this.executed = false;
      this.blockUI.stop();
    }
  }

  download(url: string): void {
    this.alertsService.info({
      title: this.userService.LOCALIZATION.downloadStarted,
      message: this.userService.LOCALIZATION.processingWaitMessage,
      delay: 10000
    });
    window.open(url, '_blank');
  }

  downloadOrder(uuid: string, pdf: boolean): void {
    this.alertsService.info({
      title: this.userService.LOCALIZATION.downloadStarted,
      message: this.userService.LOCALIZATION.processingWaitMessage,
      delay: 10000
    });
    this.transactionService.downloadTransaction(uuid, null, pdf);
  }

  print(): void {
    window.print();
  }

  endSession(): void {
    this.cartService.clearCart();
    this.router.navigate(['']); // home
  }

  register(): void {
    // Broadcast event replacement (EventEmitter or a Subject in userService)
    this.userService.triggerRegister();
  }

  emailLink(): void {
    if (this.emailSent) return;

    if (!this.isEmpty(this.input.email) && !this.isEmpty(this.input.email2)) {
      if (this.input.email !== this.input.email2) {
        this.alertsService.error({ message: "Emails do not match.", delay: 4000 });
        return;
      }

      const path = `${location.pathname}${this.router.createUrlTree(["/download-order"])}`;
      this.transactionService.emailDlLink(this.input.email ?? '', this.newPurchase.uuid, path)
        .then(() => this.emailSent = true)
        .catch(() => this.alertsService.error({ message: "Could not send email. Try again.", delay: 4000 }));
    } else {
      this.alertsService.error({ message: this.userService.LOCALIZATION.fillAllFields, delay: 4000 });
    }
  }
  private isEmpty(str: string | null): boolean {
    return !str || str == null || str == "";
  };
  
}